<template>
  <BaseButton
    v-bind="{ ...$attrs, ...computedProps }"
    variant="filled"
  >
    <template v-if="$slots.default">
      <slot />
    </template>
  </BaseButton>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import BaseButton from './BaseButton.vue';
import type { ButtonSize } from './BaseButton.vue';

interface Props {
  to?: RouteLocationRaw;
  href?: string;
  external?: boolean;
  type?: 'button' | 'submit' | 'reset';
  label?: string;
  icon?: string;
  iconRight?: string;
  tooltip?: string;
  disabledTooltip?: string;
  loading?: boolean;
  disable?: boolean;
  size?: ButtonSize;
  fullWidth?: boolean;
  pill?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  external: false,
  loading: false,
  disable: false,
  size: 'md',
  fullWidth: false,
  pill: false,
});

// Compute props object, excluding undefined values to satisfy exactOptionalPropertyTypes
const computedProps = computed(() => {
  const result: Record<string, unknown> = {
    type: props.type,
    external: props.external,
    loading: props.loading,
    disable: props.disable,
    size: props.size,
    fullWidth: props.fullWidth,
    pill: props.pill,
  };

  if (props.to !== undefined) result.to = props.to;
  if (props.href !== undefined) result.href = props.href;
  if (props.label !== undefined) result.label = props.label;
  if (props.icon !== undefined) result.icon = props.icon;
  if (props.iconRight !== undefined) result.iconRight = props.iconRight;
  if (props.tooltip !== undefined) result.tooltip = props.tooltip;
  if (props.disabledTooltip !== undefined) result.disabledTooltip = props.disabledTooltip;

  return result;
});

defineOptions({
  name: 'PrimaryButton',
  inheritAttrs: false,
});
</script>
